import React from 'react';
import { motion } from 'framer-motion';
import styles from '../styles/About.module.css';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const blockVariants = {
  hidden: { y: 20, opacity: 0, scale: 0.95 },
  visible: {
    y: 0, opacity: 1, scale: 1,
    transition: { type: "spring", stiffness: 120 }
  }
};

const experience = [
  {
    company: 'Poynter Institute',
    period: 'Jul 2019 – Present',
    roles: [
      {
        title: 'Director of MediaWise',
        period: 'Apr 2022 – Present',
        bullets: [
          'Oversees fundraising, vision, product, editorial and research',
          'Led the expansion of MediaWise into more than 12 countries, reaching more than 80 million people through the successful fundraising for, and launch of, MediaWise International',
          'Led development of innovative media literacy products, including WhatsApp courses reaching millions',
          'Partnered with Stanford University on multiple studies proving curriculum efficacy and launched the Empowering Diverse Digital Citizens Lab',
          'Designed and launched the information integrity system behind the TikTok STEM feed in four countries',
        ],
      },
      {
        title: 'Faculty',
        period: 'Sep 2024 – Present',
        bullets: [
          "Lead Poynter's AI initiatives and its AI Strategy Lab while teaching digital media literacy, fact-checking and OSINT worldwide",
          'Co-authored Poynter\'s AI ethics guide for newsrooms',
          'Organized the inaugural Poynter Summit on AI, Ethics & Journalism',
          'Developed internal, custom AI tool to assist PolitiFact reporters with jurisprudence',
          'Among the first to research crowdsourced fact-checking on Twitter, now a major trust and safety trend',
        ],
      },
      {
        title: 'MediaWise Program Manager',
        period: 'Jul 2021 – Apr 2022',
        bullets: [
          'Led day-to-day operations and managed deliverables for major grants',
          'Began diversification of fundraising strategy',
        ],
      },
      {
        title: 'Senior Multimedia Reporter',
        period: 'Jul 2019 – Jul 2021',
        bullets: [
          'Developed and led major media literacy projects for Facebook, Instagram, TikTok and YouTube, including the MediaWise Voter Project and AARP\'s MediaWise for Seniors',
          'Led trainings and webinars with Joan Lunden, Sanjay Gupta, Hari Sreenivasan and Christiane Amanpour',
          'Co-created the Seniors and Voter Project online courses',
          'Launched the MediaWise TikTok channel',
        ],
      },
    ],
  },
  {
    company: 'The Penny Hoarder',
    period: 'May 2017 – Jun 2019',
    roles: [
      {
        title: 'Data Journalist',
        period: 'May 2017 – Jun 2019',
        bullets: [
          'Pitched and wrote data-driven stories about everything from the Bureau of Labor Statistics\' Job Openings and Labor Turnover Survey to killer cane toads',
          'Built tools in R to quickly analyze government data and regularly scraped websites and documents to assist other reporters',
          'Served as informal data desk for the newsroom',
        ],
      },
    ],
  },
  {
    company: 'Observer Media Group',
    period: 'Aug 2011 – May 2017',
    roles: [
      {
        title: 'News Innovation Editor',
        period: 'Sep 2015 – May 2017',
        bullets: [
          'Led audience engagement and technology initiatives across multiple publications',
          'Wrote and edited newsletters and set digital and social strategy',
          'Drove analytics adoption and data-informed editorial decisions',
        ],
      },
      {
        title: 'Digital Editor',
        period: 'Jun 2013 – Sep 2015',
        bullets: [
          'Edited reporters\' work and led change management efforts to shift weekly reporters toward a daily digital mindset',
          'Launched multiple video series; shot, edited and starred in daily videos',
        ],
      },
      {
        title: 'News Editor, Pelican Press',
        period: 'May 2012 – Jun 2013',
        bullets: [
          'Covered county government, public records, politics and campaign finance',
        ],
      },
      {
        title: 'Tampa Bay Editor',
        period: 'Aug 2011 – May 2012',
        bullets: [
          'Daily reporting while managing digital operations for the Tampa Bay market',
        ],
      },
    ],
  },
];

const skills = {
  Technical: ['data analysis', 'R', 'web scraping', 'AI/generative AI', 'OSINT', 'Python'],
  Journalism: ['fact-checking', 'data journalism', 'verification', 'video production'],
  Leadership: ['curriculum design', 'fundraising and grants', 'training and public speaking', 'digital strategy', 'change management', 'research partnerships'],
};

const education = [
  { degree: 'M.S., Applied Economics', school: 'Florida State University', years: '2010 – 2011' },
  { degree: 'B.S., Economics', school: 'Florida State University', years: '2006 – 2010' },
];

function About() {
  return (
    <motion.div
      className={styles.container}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Decorative Background Shapes */}
      <div className={styles.shapeOne}></div>
      <div className={styles.shapeTwo}></div>

      {/* Hero Section */}
      <section className={styles.hero}>
        <motion.div className={styles.heroContent} variants={blockVariants}>
          <h1 className={styles.heroTitle}>Alex Mahadevan</h1>
          <p className={styles.heroTagline}>
            Journalist. Data nerd. Trainer. Researcher. The newsroom Swiss Army knife.
          </p>
        </motion.div>

      </section>

      {/* Impact Stats */}
      <motion.section className={styles.statsRow} variants={blockVariants}>
        <div className={styles.stat}>
          <span className={styles.statNumber}>80M+</span>
          <span className={styles.statLabel}>people reached</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statNumber}>12+</span>
          <span className={styles.statLabel}>countries</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statNumber}>1000s</span>
          <span className={styles.statLabel}>journalists trained</span>
        </div>
      </motion.section>

      {/* Featured In */}
      <motion.section className={styles.featuredSection} variants={blockVariants}>
        <h2 className={styles.featuredLabel}>Featured in</h2>
        <div className={styles.featuredList}>
          {['The New York Times', 'The Wall Street Journal', 'The Washington Post', 'PBS', 'CBS News'].map((pub) => (
            <span key={pub} className={styles.featuredPub}>{pub}</span>
          ))}
        </div>
      </motion.section>

      {/* Experience Section */}
      <section className={styles.experience}>
        <h2 className={styles.headerTitle} style={{ transform: 'rotate(1deg)' }}>Experience</h2>
        <div className={styles.timeline}>
          {experience.map((company, i) => (
            <motion.div key={i} className={styles.companyCard} variants={blockVariants}>
              <div className={styles.companyHeader}>
                <h3 className={styles.companyName}>{company.company}</h3>
                <span className={styles.companyPeriod}>{company.period}</span>
              </div>
              <div className={styles.rolesTimeline}>
                {company.roles.map((role, j) => (
                  <div key={j} className={styles.role}>
                    <div className={styles.roleHeader}>
                      <h4 className={styles.roleTitle}>{role.title}</h4>
                      <span className={styles.rolePeriod}>{role.period}</span>
                    </div>
                    <ul className={styles.roleBullets}>
                      {role.bullets.map((bullet, k) => (
                        <li key={k}>{bullet}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Skills Section */}
      <section className={styles.skillsSection}>
        <h2 className={styles.headerTitle} style={{ transform: 'rotate(-1deg)' }}>Skills</h2>
        <motion.div className={styles.skillsGrid} variants={blockVariants}>
          {Object.entries(skills).map(([category, items]) => (
            <div key={category} className={styles.skillCategory}>
              <h3 className={styles.skillCategoryTitle}>{category}</h3>
              <div className={styles.skillTags}>
                {items.map((skill) => (
                  <span key={skill} className={styles.skillBadge}>{skill}</span>
                ))}
              </div>
            </div>
          ))}
        </motion.div>
      </section>

      {/* Education Section */}
      <section className={styles.educationSection}>
        <h2 className={styles.headerTitle} style={{ transform: 'rotate(1.5deg)' }}>Education</h2>
        <div className={styles.educationGrid}>
          {education.map((edu, i) => (
            <motion.div key={i} className={styles.educationCard} variants={blockVariants}>
              <h3 className={styles.eduDegree}>{edu.degree}</h3>
              <p className={styles.eduSchool}>{edu.school}</p>
              <p className={styles.eduYears}>{edu.years}</p>
            </motion.div>
          ))}
        </div>
      </section>
    </motion.div>
  );
}

export default About;
